import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip, Button
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission } from "../../../permissions/usePermission";
import dummyImage from "../../../images/user.png";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";
import { exportNetworkMembersCsv } from "./networkExport";
import { setNetworkRestoreOnce } from "./networkStateKeys";

const COMMITTEE_ORDER = [
  "Governing Committee",
  "Operation Committee",
  "State Program Heads",
  "Other Members"

];

const COMMITTEE_MAP = {
  "Governing Committee": "Governing Committee",
  "Operational commitee": "Operation Committee",
  "StateProgram Heads": "State Program Heads",
  "Other Members": "Other Members",
};

const getFullName = (member) =>
  [member?.salutation || member?.Salutation, member?.userFirstName, member?.userMiddleName, member?.userLastName]
    .filter(Boolean)
    .join(" ");

const getMemberYear = (member) => {
  const rawDate = member?.joiningDate || member?.updatedAt;
  if (!rawDate) return "";

  const date = new Date(rawDate);
  return Number.isNaN(date.getTime()) ? "" : String(date.getFullYear());
};

const extractStringId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return extractStringId(value._id);
  }
  return "";
};

const getMemberProfileUserId = (member) =>
  extractStringId(member?.userId) ||
  extractStringId(member?.user?._id) ||
  extractStringId(member?.user) ||
  extractStringId(member?._id);

const SEC_FILTERS_STORAGE_KEY = "network_filters_sec";

const SEC = ({ members = [], allowScopedRestore = false, canOpenMemberProfile = false }) => {
  console.log("stateeee", members);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { canByKey } = usePermission();
  const canDownloadExcel = canByKey(
    "COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DOWNLOAD_EXCEL",
  );
  const navigateFrom = location.pathname.includes("committeeMembersList")
    ? "committeeMembersList"
    : "network";

  const [selectedState, setSelectedState] = useState("ALL");
  const [selectedCommittee, setSelectedCommittee] = useState("ALL");
  const [selectedYear, setSelectedYear] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (!allowScopedRestore) return;
    try {
      const saved = sessionStorage.getItem(SEC_FILTERS_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      setSelectedState(parsed?.selectedState || "ALL");
      setSelectedCommittee(parsed?.selectedCommittee || "ALL");
      setSelectedYear(parsed?.selectedYear || "ALL");
      setSearchTerm(parsed?.searchTerm || "");
      setShowSearch(Boolean(parsed?.showSearch));
    } catch (error) {
      console.warn("Unable to restore SEC filters", error);
    }
  }, [allowScopedRestore]);

  useEffect(() => {
    sessionStorage.setItem(
      SEC_FILTERS_STORAGE_KEY,
      JSON.stringify({
        selectedState,
        selectedCommittee,
        selectedYear,
        searchTerm,
        showSearch,
      }),
    );
  }, [selectedState, selectedCommittee, selectedYear, searchTerm, showSearch]);

  const states = useMemo(
    () => [...new Set(members.map((item) => item?.state).filter(Boolean))].sort(),
    [members]
  );

  const committees = useMemo(
    () =>
      [
        ...new Set(
          members
            .map((item) => item?.committee)
            .filter(Boolean).sort()
        ),
      ],
    [members]
  );
  const years = useMemo(
    () =>
      [...new Set(members.map(getMemberYear).filter(Boolean))].sort(
        (a, b) => Number(b) - Number(a)
      ),
    [members]
  );

  const getCommitteeStyle = (committee) =>
    theme.committeeStyles?.[committee] || theme.committeeStyles?.default;

  const filteredMembers = useMemo(() => {
    return members.filter((item) => {
      const normalizedCommittee =
        COMMITTEE_MAP[item?.committeeType] || item?.committee || "";
      const fullName = getFullName(item).toLowerCase();
      const memberYear = getMemberYear(item);

      const matchCommittee =
        selectedCommittee === "ALL" || normalizedCommittee === selectedCommittee;

      const matchState = selectedState === "ALL" || item?.state === selectedState;
      const matchYear = selectedYear === "ALL" || memberYear === selectedYear;
      const matchName = fullName.includes(searchTerm.toLowerCase());

      return matchCommittee && matchState && matchYear && matchName;
    });
  }, [members, searchTerm, selectedCommittee, selectedState, selectedYear]);

  const groupedMembers = useMemo(() => {
    const grouped = COMMITTEE_ORDER.reduce((acc, committee) => {
      acc[committee] = [];
      return acc;
    }, {});

    filteredMembers.forEach((item) => {
      const committee = COMMITTEE_MAP[item?.committeeType] || item?.committee;
      if (!committee) {
        return;
      }

      if (!grouped[committee]) {
        grouped[committee] = [];
      }

      grouped[committee].push(item);
    });

    return grouped;
  }, [filteredMembers]);

  const latestUpdatedAt = useMemo(() => {
    return filteredMembers.reduce((acc, item) => {
      const ts = item?.updatedAt || item?.joiningDate;
      if (!ts) return acc;

      const date = new Date(ts);
      return !acc || date > acc ? date : acc;
    }, null);
  }, [filteredMembers]);




  const formatUpdatedDate = (date) => {
    if (!date) return "";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getLatestUpdatedAt = (members = []) =>
    members.reduce((acc, member) => {
      const ts = member?.updatedAt || member?.joiningDate;
      if (!ts) return acc;

      const date = new Date(ts);
      if (Number.isNaN(date.getTime())) {
        return acc;
      }

      return !acc || date > acc ? date : acc;
    }, null);


  const visibleUpdatedAt = useMemo(
    () => getLatestUpdatedAt(filteredMembers),
    [filteredMembers]
  );

  const handleExport = () => {
    exportNetworkMembersCsv(filteredMembers, "network_sec_members");
  };



  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, mt: 3 }}>
      <Box
      // sx={{
      //   borderRadius: 4,
      //   px: isMobile ? 1 : 2,
      // }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "column", md: "row" },
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "nowrap",
              width: "100%",
              justifyContent: "flex-start",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              select
              size="small"
              label="State"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              sx={{ width: { xs: "100%", sm: "32%", md: 180, lg: 220 } }}
            >
              <MenuItem value="ALL">All States</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Committee"
              value={selectedCommittee}
              onChange={(e) => setSelectedCommittee(e.target.value)}
              sx={{ width: { xs: "100%", sm: "32%", md: 180, lg: 220 } }}
            >
              <MenuItem value="ALL">All</MenuItem>
              {committees.map((committee) => (
                <MenuItem key={committee} value={committee}>
                  {committee}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ width: { xs: "100%", sm: "32%", md: 180, lg: 220 } }}
            >
              <MenuItem value="ALL">All Years</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
           
           
           {canDownloadExcel && (
             <Tooltip title="Download Excel" arrow>
               <span>
                 <Button
                   variant="contained"
                   color="primary"
                   startIcon={<FileDownloadOutlinedIcon />}
                   onClick={handleExport}
                   disabled={!filteredMembers.length}
                   aria-label="Download Excel"
                   sx={{
                     textTransform: "none",
                     //borderRadius: 2,
                   }}
                 >
                   Download
                 </Button>
               </span>
             </Tooltip>
           )}


            {showSearch ? (
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                sx={{ width: { xs: "100%", md: 220 } }}
              />
            ) : (
              <IconButton onClick={() => setShowSearch(true)}>
                <SearchIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {COMMITTEE_ORDER.map((committee) => {
        const membersList = groupedMembers[committee];
        if (!membersList || membersList.length === 0) return null;

        const style = getCommitteeStyle(committee);

        return (
          <Box
            key={committee}
            sx={{
              borderRadius: 4,
              mb: 4,
              px: 2,
              py: 2,
              background: style?.gradient,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h5" fontWeight={600}>
                {committee}
              </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {membersList.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member._id || getFullName(member)}>
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid #e6e6e6",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      cursor: canOpenMemberProfile ? "pointer" : "default",
                      "&:hover": canOpenMemberProfile
                        ? {
                          boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                        }
                        : {},
                    }}
                    onClick={() => {
                      const profileUserId = getMemberProfileUserId(member);
                      if (canOpenMemberProfile && profileUserId) {
                        setNetworkRestoreOnce();
                        navigate(`/profile/${profileUserId}`, {
                          state: {
                            mode: "view",
                            navigateFrom,
                            canOpenMemberProfile,
                          },
                        });
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        overflow: "hidden",
                        background: style?.solid,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={getPublicS3Url(member?.userImageURL) || dummyImage}
                        alt={getFullName(member)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography fontWeight={700}>{getFullName(member)}</Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "#666", mt: 0.5 }}
                      >
                        {member?.position || ""}
                      </Typography>


                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {formatUpdatedDate(visibleUpdatedAt)}
        </Typography>
      </Box>

    </Box>
  );
};

export default SEC;
